import { secondAxios } from "../../../../../../api/axios";

const quoteIdent = (value) => `"${String(value).replace(/"/g, '""')}"`;

const CLIENT_REGISTRY_DB = "piping";
const CLIENT_REGISTRY_TABLE = "__client_data_clients";

const extractRows = (response) =>
  Array.isArray(response?.data) ? response.data : [];

const normalizeSqlType = (value) => {
  const type = String(value || "TEXT").trim().toUpperCase();
  // SQLite type declarations are intentionally flexible, but keep copied
  // declarations to a safe identifier/size character set before embedding DDL.
  return /^[A-Z0-9_ (),.]+$/.test(type) ? type : "TEXT";
};

export async function listTables(dbName) {
  if (!dbName) return [];

  const response = await secondAxios.post("/sqlite-db/query", {
    dbName,
    sql: "SELECT name FROM sqlite_master WHERE type = 'table' AND name NOT LIKE 'sqlite_%' ORDER BY name",
  });

  return extractRows(response)
    .map((row) => row?.name)
    .filter((name) => Boolean(name) && name !== CLIENT_REGISTRY_TABLE);
}

const ensureClientRegistry = async () => {
  await secondAxios.post("/sqlite-db/run", {
    dbName: CLIENT_REGISTRY_DB,
    sql: `CREATE TABLE IF NOT EXISTS ${quoteIdent(CLIENT_REGISTRY_TABLE)} (
      "label" TEXT NOT NULL,
      "value" TEXT NOT NULL UNIQUE,
      "db_suffix" TEXT NOT NULL UNIQUE,
      "created_at" TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP
    )`,
    params: [],
  });
};

export async function listRegisteredClients() {
  await ensureClientRegistry();

  const response = await secondAxios.post("/sqlite-db/query", {
    dbName: CLIENT_REGISTRY_DB,
    sql: `SELECT "label", "value", "db_suffix" FROM ${quoteIdent(
      CLIENT_REGISTRY_TABLE
    )} ORDER BY "label" COLLATE NOCASE`,
    params: [],
  });

  return extractRows(response)
    .filter((row) => row?.label && row?.value && row?.db_suffix)
    .map((row) => ({
      label: row.label,
      value: row.value,
      type: "client",
      dbSuffix: row.db_suffix,
      userCreated: true,
    }));
}

export async function registerClient({ label, value, dbSuffix }) {
  await ensureClientRegistry();

  return secondAxios.post("/sqlite-db/run", {
    dbName: CLIENT_REGISTRY_DB,
    sql: `INSERT INTO ${quoteIdent(
      CLIENT_REGISTRY_TABLE
    )} ("label", "value", "db_suffix") VALUES (?, ?, ?)`,
    params: [label, value, dbSuffix],
  });
}

/**
 * Return the complete user-defined SQLite schema without reading table rows.
 * Executing these statements in a new database preserves table constraints,
 * indexes, views, and triggers while keeping the new database empty.
 */
export async function getDatabaseSchema(dbName) {
  if (!dbName) return [];

  const response = await secondAxios.post("/sqlite-db/query", {
    dbName,
    sql: `SELECT type, name, tbl_name AS tableName, sql
          FROM sqlite_master
          WHERE sql IS NOT NULL
            AND name NOT LIKE 'sqlite_%'
            AND type IN ('table', 'index', 'view', 'trigger')
          ORDER BY CASE type
            WHEN 'table' THEN 1
            WHEN 'view' THEN 2
            WHEN 'index' THEN 3
            WHEN 'trigger' THEN 4
            ELSE 5
          END, name`,
    params: [],
  });

  return extractRows(response).filter(
    (entry) =>
      entry?.sql &&
      entry?.name !== CLIENT_REGISTRY_TABLE &&
      entry?.tableName !== CLIENT_REGISTRY_TABLE
  );
}

export async function createDatabaseFromSchema(
  dbName,
  schemaEntries,
  onProgress
) {
  if (!dbName) throw new Error("A target database name is required.");
  if (!schemaEntries?.length) {
    throw new Error("The selected template database has no schema to copy.");
  }

  for (let index = 0; index < schemaEntries.length; index += 1) {
    const entry = schemaEntries[index];
    onProgress?.({
      current: index + 1,
      total: schemaEntries.length,
      name: entry.name,
      type: entry.type,
    });

    await secondAxios.post("/sqlite-db/run", {
      dbName,
      sql: entry.sql,
      params: [],
    });
  }
}

export async function getTableColumns(dbName, tableName) {
  if (!dbName || !tableName) return [];

  const response = await secondAxios.post("/sqlite-db/query", {
    dbName,
    sql: `PRAGMA table_info(${quoteIdent(tableName)})`,
  });

  return extractRows(response)
    .filter((column) => column?.name)
    .map((column) => ({
      name: column.name,
      type: column.type || "TEXT",
      notNull: Boolean(column.notnull),
      defaultValue: column.dflt_value,
      primaryKeyOrder: Number(column.pk || 0),
    }));
}

// Some imported sqlite tables carry a leftover header row as actual data
// (every column's value equals that column's own name) — drop it.
const isHeaderRow = (row) => {
  const keys = Object.keys(row).filter((key) => key !== "id");
  if (!keys.length) return false;
  return keys.every((key) => String(row[key] ?? "").trim() === key.trim());
};

export async function getTableRows(dbName, tableName) {
  if (!dbName || !tableName) return [];

  const response = await secondAxios.post("/sqlite-db/query", {
    dbName,
    sql: `SELECT rowid AS id, * FROM ${quoteIdent(tableName)}`,
  });
  return extractRows(response).filter((row) => !isHeaderRow(row));
}

export async function updateTableRow(dbName, tableName, row) {
  const { id, ...columns } = row;
  const keys = Object.keys(columns);
  if (!dbName || !tableName || id == null || !keys.length) return null;

  const setClause = keys.map((key) => `${quoteIdent(key)} = ?`).join(", ");
  const params = [...keys.map((key) => columns[key]), id];

  return secondAxios.post("/sqlite-db/run", {
    dbName,
    sql: `UPDATE ${quoteIdent(tableName)} SET ${setClause} WHERE rowid = ?`,
    params,
  });
}

/**
 * Create an empty SQLite table.
 *
 * columns can be either strings (manual columns) or schema objects returned by
 * getTableColumns(). Template copies preserve the declared SQLite column type,
 * NOT NULL/default metadata, and primary-key shape. Data is intentionally not
 * copied: the selected table is used as a schema template only.
 */
export async function createTable(dbName, tableName, columns) {
  if (!dbName) throw new Error("A database must be selected.");

  const cleanTableName = String(tableName || "").trim();
  if (!cleanTableName) throw new Error("A table name is required.");

  const normalizedColumns = (columns || [])
    .map((column) =>
      typeof column === "string"
        ? { name: column.trim(), type: "TEXT" }
        : {
            ...column,
            name: String(column?.name || "").trim(),
            type: column?.type || "TEXT",
          }
    )
    .filter((column) => column.name);

  if (!normalizedColumns.length) {
    throw new Error("Add at least one column before creating the table.");
  }

  const lowerNames = normalizedColumns.map((column) => column.name.toLowerCase());
  if (new Set(lowerNames).size !== lowerNames.length) {
    throw new Error("Column names must be unique.");
  }

  const primaryKeyColumns = normalizedColumns
    .filter((column) => Number(column.primaryKeyOrder || 0) > 0)
    .sort(
      (a, b) =>
        Number(a.primaryKeyOrder || 0) - Number(b.primaryKeyOrder || 0)
    );

  const columnSql = normalizedColumns.map((column) => {
    const pieces = [quoteIdent(column.name), normalizeSqlType(column.type)];

    // Inline PRIMARY KEY is safe only for a single-column PK. For compound
    // primary keys we add a table-level constraint below.
    if (primaryKeyColumns.length === 1 && primaryKeyColumns[0].name === column.name) {
      pieces.push("PRIMARY KEY");
    }
    if (column.notNull) pieces.push("NOT NULL");
    if (column.defaultValue !== undefined && column.defaultValue !== null) {
      pieces.push(`DEFAULT ${column.defaultValue}`);
    }

    return pieces.join(" ");
  });

  if (primaryKeyColumns.length > 1) {
    columnSql.push(
      `PRIMARY KEY (${primaryKeyColumns
        .map((column) => quoteIdent(column.name))
        .join(", ")})`
    );
  }

  return secondAxios.post("/sqlite-db/run", {
    dbName,
    sql: `CREATE TABLE ${quoteIdent(cleanTableName)} (${columnSql.join(", ")})`,
    params: [],
  });
}
